import { CommonModule } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  CUSTOM_ELEMENTS_SCHEMA,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { NavigationEnd, Router, RouterModule } from '@angular/router';
import { MobileMenuComponent, type NavItem } from '@commons/mobile-menu/mobile-menu.component';
import { ContactModalComponent } from '@commons/molecules/contact-modal/contact-modal.component';
import { environment } from '@environment';
import { FeaturesRoutingEnum } from '@features/features-routing.enum';
import { HelpFormRoutingEnum } from '@features/help-form/help-form-routing.enum';
import { Store } from '@ngrx/store';
import { isClient } from '@stores/dossier/dossier-ngrx.selector';
import {
  blogAdvicesEnabled,
  blogFaqEnabled,
  blogFinancialAidEnabled,
} from '@stores/features-flags/features-flags-ngrx.selector';
import { selectRouteData } from '@stores/router/router.selectors';
import { contactEmailKeys, hasFeatureFlagTrustpilot } from '@stores/services/services.selector';
import { identityCard, user } from '@stores/session/session-ngrx.selector';
import { suggestionClear } from '@stores/suggestion/suggestion-ngrx.action';
import { ModalService } from '@wizbii-money/angular';
import { info, jwtDelete } from '@wizbii-utils/angular/stores';
import { SuggestionsWebservice } from '@wizbii-utils/angular/webservices';
import { trackEvent } from '@wizbii/utils/tracking';
import { BehaviorSubject, of } from 'rxjs';
import { filter, map, switchMap, tap } from 'rxjs/operators';
import { MoneyHeaderComponent } from '@commons/organisms/header/header.component';

@Component({
  selector: 'app-default-layout',
  templateUrl: './default-layout.component.html',
  styleUrls: ['./default-layout.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, RouterModule, MatIconModule, MobileMenuComponent, MoneyHeaderComponent],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
})
export class DefaultLayoutComponent implements OnInit, OnDestroy {
  readonly #destroyRef = inject(DestroyRef);
  readonly #store = inject(Store);
  readonly #router = inject(Router);
  readonly #modalService = inject(ModalService);
  readonly #suggestionsWebService = inject(SuggestionsWebservice);

  readonly baseHref = environment.baseHref;
  readonly contactModalProvider = 'WizbiiMoney.toggleModal';
  readonly appId = environment.applicationId;
  readonly accountApiUrl = environment.api.account;
  readonly locale = environment.i18n.locale;
  readonly accountUrl = environment.urls.account.base;
  readonly footerApiDomain = environment.domain.wizbii;

  readonly isTrustpilotEnabled = this.#store.selectSignal(hasFeatureFlagTrustpilot);
  readonly isClient = this.#store.selectSignal(isClient);
  readonly identityCard = this.#store.selectSignal(identityCard);
  readonly user = this.#store.selectSignal(user);
  readonly defaultEmailKey = this.#store.selectSignal(contactEmailKeys(environment.contactEmailKey.base));
  readonly premiumEmailKey = this.#store.selectSignal(contactEmailKeys(environment.contactEmailKey.premium));

  readonly routeData = this.#store.selectSignal(selectRouteData);

  showHeader = signal(this.routeData().showHeader ?? true);

  readonly showWzbFooter = computed(() => {
    const showWzbFooter = this.routeData().showWzbFooter;
    return typeof showWzbFooter === 'boolean' ? showWzbFooter : true;
  });

  readonly showMobileNav = computed(() => {
    const showMobileNav = this.routeData().showMobileNav;
    return typeof showMobileNav === 'boolean' ? showMobileNav : true;
  });

  readonly blogAdvicesEnabled = this.#store.selectSignal(blogAdvicesEnabled);
  readonly blogFinancialAidEnabled = this.#store.selectSignal(blogFinancialAidEnabled);
  readonly blogFaqEnabled = this.#store.selectSignal(blogFaqEnabled);

  @Input() suggestionSetId?: string;

  menuItems = computed<any>(() => {
    const blogLinks = [
      ...(this.blogAdvicesEnabled()
        ? [
            {
              type: 'link',
              label: $localize`:@@money.header-menu.advices:Nos conseils`,
              href: `${this.baseHref}${FeaturesRoutingEnum.Advices}`,
              click: () => {
                trackEvent('Home Navigation', 'Click Nos conseils');
              },
            },
          ]
        : []),
      ...(this.blogFinancialAidEnabled()
        ? [
            {
              type: 'link',
              label: $localize`:@@money.header-menu.helps-directory:Annuaire des aides`,
              href: `${this.baseHref}${FeaturesRoutingEnum.HelpsListing}`,
              click: () => {
                trackEvent('Home Navigation', 'Click Toutes les aides');
              },
            },
          ]
        : []),
      ...(this.blogFaqEnabled()
        ? [
            {
              type: 'link',
              label: $localize`:@@money.header-menu.faq:FAQ`,
              href: `${this.baseHref}${FeaturesRoutingEnum.Faq}`,
              click: () => {
                trackEvent('Home Navigation', 'Click FAQ');
              },
            },
          ]
        : []),
    ];

    return [
      [
        {
          type: 'routerLink',
          label: $localize`:@@money.header-menu.helps:Mes aides`,
          routerLink: ['/', 'dashboard'],
          click: () => {
            trackEvent('Home Navigation', 'Click Mes aides');
          },
        },
        {
          type: 'routerLink',
          label: $localize`:@@money.header-menu.situation:Ma situation`,
          routerLink: ['/', 'helps', 'summary'],
          click: () => {
            trackEvent('Home Navigation', 'Click Ma situation');
          },
        },
        {
          type: 'link',
          label: $localize`:@@money.header-menu.profile:Mon profil`,
          href: this.accountUrl,
          isExternal: true,
          click: () => {
            trackEvent('Home Navigation', 'Click Mon profil');
          },
        },
      ],
      [
        ...blogLinks,
        {
          type: 'button',
          label: $localize`:@@wzb-money-header.menu-items.contact-us:Nous contacter`,
          click: () => {
            this.openDialog();
          },
        },
      ],
      [
        {
          type: 'button',
          label: $localize`:@@money.header-menu.logout:Se déconnecter`,
          extraClass: 'menu__item--signout',
          click: () => {
            trackEvent('Home Navigation', 'Click Se déconnecter');
            this.#store.dispatch(jwtDelete());
            window.open(`${environment.urls.account.logout}${window.location.origin}`, '_self');
          },
        },
      ],
    ];
  });

  contactModalConfig = computed(() => {
    return {
      appId: environment.applicationId,
      contactEmailKey: this.isClient() ? this.premiumEmailKey() : this.defaultEmailKey(),
      profile: {
        firstName: this.identityCard().firstName,
        lastName: this.identityCard().lastName,
        phone: this.identityCard().mobile,
        email: this.user()?.username,
      },
      locale: this.locale,
    };
  });

  trackEvent = trackEvent;

  FeaturesRoutingEnum = FeaturesRoutingEnum;
  processingDataClear$ = new BehaviorSubject<boolean>(false);

  navItems: NavItem[] = [
    {
      label: $localize`:@@default-layout.menu.helps:Mes aides`,
      link: ['/', FeaturesRoutingEnum.Dashboard],
      click: (): void => {
        trackEvent('Mobile navbar', 'Click Mes aides');
      },
      icon: 'euro-circle',
    },
    {
      label: $localize`:@@default-layout.menu.situation:Ma situation`,
      link: ['/', FeaturesRoutingEnum.HelpForm, HelpFormRoutingEnum.Summary],
      click: (): void => {
        trackEvent('Mobile navbar', 'Click Ma situation');
      },
      icon: 'user-circle',
    },
    {
      label: $localize`:@@default-layout.menu.contact-us:Contact`,
      click: (): void => {
        this.openDialog();
      },
      icon: 'envelope',
    },
  ];

  get isHelpsRoute(): boolean {
    return this.#router.url === `/${FeaturesRoutingEnum.HelpsListing}`;
  }

  ngOnInit(): void {
    this.#router.events.subscribe({
      next: (event) => {
        if (event instanceof NavigationEnd) {
          this.showHeader.set(this.routeData().showHeader ?? true);
        }
      },
    });
    document.addEventListener(this.contactModalProvider, () => {
      this.openDialog();
    });
  }

  resetForm(): void {
    this.trackEvent('Dashboard', 'Click Restart Header Money');
    this.#store
      .select(info)
      .pipe(
        takeUntilDestroyed(this.#destroyRef),
        tap(() => {
          this.processingDataClear$.next(true);
        }),
        filter((info) => !!info),
        map((info) => info['user-id']),
        switchMap((userId) => {
          if (!userId) {
            return of(undefined);
          }
          return this.#suggestionsWebService.clearUserData(userId, 'member');
        }),
        map(() => {
          this.#store.dispatch(suggestionClear());
        })
      )
      .subscribe(() => {
        this.processingDataClear$.next(false);
        this.#router.navigate(['/', FeaturesRoutingEnum.HelpForm]);
      });
  }

  openDialog(): void {
    trackEvent('Home Navigation', 'Click Contactez-nous');

    this.#modalService
      .openModal(ContactModalComponent, {
        data: {
          contactFormData: {
            appId: this.contactModalConfig().appId,
            recipientEmailKey: this.contactModalConfig().contactEmailKey,
            firstName: this.contactModalConfig().profile.firstName,
            lastName: this.contactModalConfig().profile.lastName,
            email: this.contactModalConfig().profile.email,
            phone: this.contactModalConfig().profile.phone,
          },
        },
      })
      .subscribe();
  }

  ngOnDestroy(): void {
    document.removeEventListener(this.contactModalProvider, () => {
      this.openDialog();
    });
  }
}
